"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { ArithmeticSheet } from "@/components/tools/worksheets-math/arithmetic-sheet";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import {
  CommonControls,
  defaultSettings,
  UnmetNotice,
  useCommonSettings,
  useWorksheetSeed,
} from "@/components/tools/worksheets-math/worksheet-kit";
import {
  generateMultiplication,
  type MissingMode,
  type MultiplicationOptions,
} from "@/lib/tool-kit/worksheets/arithmetic";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const MULTIPLICATION_WORKSHEET_GENERATOR_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Multiplication Practice",
  modeLabel: "Practice type",
  modes: { tables: "Times tables", digits: "Multi-digit" },
  tablesLabel: "Times tables to practise",
  tablePresets: {
    low: "2–5",
    mid: "6–9",
    all: "2–12",
    clear: "Clear",
  },
  otherFromLabel: "Other factor from",
  otherToLabel: "Other factor to",
  mixOrder: "Mix the order (6 × 3 and 3 × 6)",
  unknownLabel: "What the student finds",
  unknowns: {
    answer: "The product (3 × 6 = ?)",
    missing: "A missing factor (3 × ? = 18)",
    mixed: "A mix of both",
  },
  unknownHint: "Missing-factor problems use the one-line layout.",
  firstDigitsLabel: "Digits in the first factor",
  secondDigitsLabel: "Digits in the second factor",
  noZeros: "Avoid zeros in the factors (easier)",
  noTables: "Choose at least one times table.",
} as const;

const TABLE_NUMBERS = Array.from({ length: 13 }, (_, i) => i);
const FACTOR_CHOICES = Array.from({ length: 13 }, (_, i) => String(i));
const DIGIT_CHOICES = ["1", "2", "3", "4"];

type Mode = "tables" | "digits";

function range(from: number, to: number): number[] {
  return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}

export default function MultiplicationWorksheetGeneratorWidget() {
  const strings = useToolUi(MULTIPLICATION_WORKSHEET_GENERATOR_STRINGS);
  const [settings, update] = useCommonSettings(
    defaultSettings({ count: 30, layout: "vertical" })
  );
  const seed = useWorksheetSeed();
  const [mode, setMode] = useState<Mode>("tables");
  const [tables, setTables] = useState<number[]>(range(2, 9));
  const [otherMin, setOtherMin] = useState(1);
  const [otherMax, setOtherMax] = useState(10);
  const [mixOrder, setMixOrder] = useState(false);
  const [missing, setMissing] = useState<MissingMode>("answer");
  const [firstDigits, setFirstDigits] = useState(3);
  const [secondDigits, setSecondDigits] = useState(2);
  const [noZeros, setNoZeros] = useState(false);

  const oneLine = settings.layout === "horizontal";
  const options: MultiplicationOptions =
    mode === "tables"
      ? {
          kind: "tables",
          count: settings.count,
          tables,
          otherMin: Math.min(otherMin, otherMax),
          otherMax: Math.max(otherMin, otherMax),
          mixOrder,
          missing: oneLine ? missing : "answer",
        }
      : {
          kind: "digits",
          count: settings.count,
          firstDigits,
          secondDigits,
          noZeroDigits: noZeros,
        };
  const noTablesChosen = mode === "tables" && tables.length === 0;
  const generated = noTablesChosen
    ? { problems: [], unmet: 0 }
    : generateMultiplication(options, createRng(seed.seed));

  function switchMode(next: Mode) {
    setMode(next);
    update(
      next === "tables"
        ? { count: 30, layout: "vertical", columns: 0 }
        : { count: 12, layout: "vertical", columns: 0 }
    );
  }

  function toggleTable(value: number) {
    setTables((current) =>
      current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value].sort((a, b) => a - b)
    );
  }

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <PillGroup
          label={strings.modeLabel}
          name="mw-mode"
          onChange={switchMode}
          options={(["tables", "digits"] as const).map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        {mode === "tables" ? (
          <div className="space-y-4">
            <fieldset className="space-y-2">
              <legend className={LABEL}>{strings.tablesLabel}</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {TABLE_NUMBERS.map((value) => (
                  <Checkbox
                    checked={tables.includes(value)}
                    key={value}
                    label={String(value)}
                    onChange={() => toggleTable(value)}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className={BUTTON_SMALL}
                  onClick={() => setTables(range(2, 5))}
                  type="button"
                >
                  {strings.tablePresets.low}
                </button>
                <button
                  className={BUTTON_SMALL}
                  onClick={() => setTables(range(6, 9))}
                  type="button"
                >
                  {strings.tablePresets.mid}
                </button>
                <button
                  className={BUTTON_SMALL}
                  onClick={() => setTables(range(2, 12))}
                  type="button"
                >
                  {strings.tablePresets.all}
                </button>
                <button
                  className={BUTTON_SMALL}
                  onClick={() => setTables([])}
                  type="button"
                >
                  {strings.tablePresets.clear}
                </button>
              </div>
            </fieldset>
            <div className="grid gap-4 sm:grid-cols-3">
              <SelectField
                id="mw-other-from"
                label={strings.otherFromLabel}
                onChange={(value) => setOtherMin(Number(value))}
                options={FACTOR_CHOICES.map((v) => ({ value: v, label: v }))}
                value={String(otherMin)}
              />
              <SelectField
                id="mw-other-to"
                label={strings.otherToLabel}
                onChange={(value) => setOtherMax(Number(value))}
                options={FACTOR_CHOICES.map((v) => ({ value: v, label: v }))}
                value={String(otherMax)}
              />
              <SelectField
                hint={oneLine ? undefined : strings.unknownHint}
                id="mw-unknown"
                label={strings.unknownLabel}
                onChange={setMissing}
                options={(["answer", "missing", "mixed"] as const).map(
                  (value) => ({ value, label: strings.unknowns[value] })
                )}
                value={oneLine ? missing : "answer"}
              />
            </div>
            <Checkbox
              checked={mixOrder}
              label={strings.mixOrder}
              onChange={setMixOrder}
            />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField
              id="mw-first-digits"
              label={strings.firstDigitsLabel}
              onChange={(value) => setFirstDigits(Number(value))}
              options={DIGIT_CHOICES.map((v) => ({ value: v, label: v }))}
              value={String(firstDigits)}
            />
            <SelectField
              id="mw-second-digits"
              label={strings.secondDigitsLabel}
              onChange={(value) => setSecondDigits(Number(value))}
              options={DIGIT_CHOICES.slice(0, 3).map((v) => ({
                value: v,
                label: v,
              }))}
              value={String(secondDigits)}
            />
            <div className="flex items-end pb-2">
              <Checkbox
                checked={noZeros}
                label={strings.noZeros}
                onChange={setNoZeros}
              />
            </div>
          </div>
        )}
        <CommonControls
          idPrefix="mw"
          onChange={update}
          seed={seed}
          settings={settings}
          strings={strings}
        />
        {noTablesChosen ? <ErrorNotice>{strings.noTables}</ErrorNotice> : null}
        <UnmetNotice strings={strings} unmet={generated.unmet} />
      </div>
      {generated.problems.length > 0 ? (
        <ArithmeticSheet
          problems={generated.problems}
          seed={seed.seed}
          settings={settings}
          strings={strings}
          title={settings.title.trim() || strings.defaultTitle}
          workRows={mode === "digits" && secondDigits > 1 ? secondDigits : 0}
        />
      ) : null}
    </div>
  );
}
